import { routingProfileServiceEnvironment } from '../routing-profile-service-environment';
import { requiredHeaders } from '../../../models/required-headers';
import { selectData } from '../../../utils/select-data';
import { withJwt } from '../../../utils/with-jwt';
import { routingProfileSchema } from '../schemas/routing-profile.schema';
import { createRequestEnumPreprocessor } from '../../../utils/create-request-enum-preprocessor';
import { unitsOfMeasurementOptions } from '../../../schemas/units-of-measurement.schema';
import { coPilotOptionsSchema } from '../schemas/copilot-options.schema';
import { displayRestrictionTypeOptions } from '../schemas/display-restriction-type.schema';
import { routingOptionsSchema } from '../schemas/routing-options.schema';
import { ferriesOptions } from '../schemas/ferries.schema';
import { tollRoadTypeOptions } from '../schemas/toll-road-type.schema';
import { routingTypeOptions } from '../schemas/routing-type.schema';
import { sideOfStreetAdherenceLevelOptions } from '../schemas/side-of-street-adherence-level.schema';
import { vehicleOptionsSchema } from '../schemas/vehicle-options.schema';
import { hazmatTypeOptions } from '../schemas/hazmat-type.schema';
import { tunnelCategoryOptions } from '../schemas/tunnel-category.schema';
import { parseRequestSchema } from '../../../utils/parse-request-schema';
import { z } from 'zod/v4';
import { fromFetch } from 'rxjs/fetch';

const requestSchema = routingProfileSchema
  .omit({
    routingProfileId: true,
  })
  .partial()
  .required({
    name: true,
    vehicleType: true,
    isAccountDefault: true,
  });

const schemaParser = routingProfileSchema.extend({
  routingProfileId: z.number().optional(),
  units: createRequestEnumPreprocessor(unitsOfMeasurementOptions).optional(),
  copilotOptions: coPilotOptionsSchema
    .extend({
      displayRestrictions: createRequestEnumPreprocessor(
        displayRestrictionTypeOptions
      ),
    })
    .optional(),
  routingOptions: routingOptionsSchema
    .extend({
      ferries: createRequestEnumPreprocessor(ferriesOptions),
      tollRoads: createRequestEnumPreprocessor(tollRoadTypeOptions),
      routeType: createRequestEnumPreprocessor(routingTypeOptions),
      sideOfStreetAdherenceLevel: createRequestEnumPreprocessor(
        sideOfStreetAdherenceLevelOptions
      ),
    })
    .optional(),
  vehicleOptions: vehicleOptionsSchema
    .extend({
      hazmatType: createRequestEnumPreprocessor(hazmatTypeOptions).optional(),
      tunnelCategory: createRequestEnumPreprocessor(
        tunnelCategoryOptions
      ).optional(),
    })
    .optional(),
});

/**
 * Create a new Routing Profile
 * @param request Routing Profile to create
 * @returns A Data object containing the newly created Routing Profile details
 */
export function postRoutingProfiles(
  this: unknown,
  request: z.infer<typeof requestSchema>
) {
  return withJwt((jwtAuthHeaders) =>
    fromFetch(
      new Request(
        routingProfileServiceEnvironment.resolve(this).routingProfilesUrl,
        {
          method: 'POST',
          headers: {
            ...requiredHeaders,
            ...jwtAuthHeaders,
          },
          body: parseRequestSchema(schemaParser, request),
        }
      )
    )
  ).pipe(
    selectData(
      z.object({
        message: z.string(),
        data: routingProfileSchema,
        warnings: z.array(z.string()),
      })
    )
  );
}
